import {
  ComposerSection,
  ComposerDocument,
  ComposerSectionProps,
  UsableComposerSectionProps,
  ComposerValidatorContext,
  ComposerParts,
  ValidatorErrorWithIndex,
} from '../types';
import { isComposerSection } from './is-composer-section';

type SectionPartMap = Record<string, ComposerSection<ComposerSectionProps>>;

const getSectionPartMap = (parts: ComposerParts) => {
  const sectionPartMap: SectionPartMap = {};

  for (const part of Object.values(parts)) {
    if (!isComposerSection(part)) continue;
    sectionPartMap[part.craft.props.sectionType] = part;
  }

  return sectionPartMap;
};

export const validateComposerDocument = async (
  document: ComposerDocument,
  parts: ComposerParts,
  context: ComposerValidatorContext = {},
  options?: {
    sectionIndexes?: number[];
  }
): Promise<ValidatorErrorWithIndex[]> => {
  const sectionPartMap = getSectionPartMap(parts);
  const sections = document.sections ?? [];
  const sectionIndexFilter = options?.sectionIndexes ? new Set(options.sectionIndexes) : undefined;

  const sectionValidationPromises = sections.map(async (section, sectionIndex) => {
    if (sectionIndexFilter && !sectionIndexFilter.has(sectionIndex)) return [] as ValidatorErrorWithIndex[];

    const { sectionType, ...sectionProps } = section;
    const part = sectionPartMap[sectionType];

    if (!part) return [] as ValidatorErrorWithIndex[];

    const validator = part.craft.related.validator;
    if (!validator) return [] as ValidatorErrorWithIndex[];

    const sectionErrors: ValidatorErrorWithIndex[] = [];

    await validator.validate({
      errors: validator.errors,
      reportError: (error, count = 1) => {
        sectionErrors.push({
          code: error.code,
          count,
          sectionIndex,
          sectionType,
        });
      },
      props: sectionProps as UsableComposerSectionProps<ComposerSectionProps>,
      context,
    });

    return sectionErrors;
  });

  const errors = await Promise.all(sectionValidationPromises);
  return errors.flat();
};
